import React, { useCallback, useMemo, useState } from "react";
import {
  View,
  StyleSheet,
  Image,
  ActivityIndicator,
  TouchableOpacity,
  Text,
  SectionList,
} from "react-native";

// Import icons
import { ChevronDown, ChevronRight } from "lucide-react";

// Import utilities
import { trackEvent } from "../../../../utils/mixpanel";
import DateControlHeader from "./DateControlHeader";
import { SearchInput, SecondaryButton } from "../../../common/native";
import EMRIntegrationRequestPrompt from "./components/EMRIntegrationRequestPrompt";

const PatientListView = ({
  scheduleData,
  isLoading,
  isError,
  ehrConnected,
  filteredScheduleData,
  searchTerm,
  setSearchTerm,
  handleSearchTermChange,
  isSearching,
  searchMode,
  setSearchMode,
  setSearchResults,
  scrollRef,
  headerHeight,
  refreshSchedule,
  loadMoreDays,
  renderSectionHeader,
  renderPatientItem,
  viewLabel,
  toggleViewMode,
  viewMode,
  session,
  onOpenRangePicker,
  onShiftRange,
  onTodayPress,
  rangeOffset,
  visibleSectionIndex,
  setVisibleSectionIndex,
  scrollY,
  setScrollY,
  tipsEnabled,
  onOpenQuickRecord,
  useDummyData,
  onUseDemoDataForTesting,
}) => {
  const [collapsedSections, setCollapsedSections] = useState({});

  const isCollapsibleSection = useCallback((section) => {
    const nonCollapsibleSectionDates = new Set([
      "session-warning",
      "search-results",
      "no-results",
      "error",
    ]);
    return !nonCollapsibleSectionDates.has(section?.date);
  }, []);

  const displayedSections = useMemo(() => {
    if (!Array.isArray(filteredScheduleData)) return [];

    return filteredScheduleData.map((section) => {
      if (!isCollapsibleSection(section)) {
        return section;
      }

      const itemCount = Array.isArray(section.data) ? section.data.length : 0;
      const isCollapsed = collapsedSections[section.date];
      if (!isCollapsed) {
        return section;
      }

      return {
        ...section,
        data: [],
        __itemCount: itemCount,
      };
    });
  }, [filteredScheduleData, collapsedSections, isCollapsibleSection]);

  const toggleSectionCollapsed = useCallback((sectionDate) => {
    setCollapsedSections((prev) => ({
      ...prev,
      [sectionDate]: !prev[sectionDate],
    }));
  }, []);

  const hasAnyAppointments = useMemo(() => {
    if (!Array.isArray(scheduleData)) return false;
    return scheduleData.some(
      (section) => Array.isArray(section?.data) && section.data.length > 0,
    );
  }, [scheduleData]);

  const showEmptyState = !hasAnyAppointments && !searchMode;
  const showScheduleOrSearchResults = !showEmptyState;

  const renderSectionHeaderWithCollapse = useCallback(
    ({ section }) => {
      if (!isCollapsibleSection(section)) {
        return renderSectionHeader({ section });
      }

      const isCollapsed = Boolean(collapsedSections[section.date]);
      const itemCount =
        typeof section.__itemCount === "number"
          ? section.__itemCount
          : section.data?.length || 0;
      const itemLabel = itemCount === 1 ? "appointment" : "appointments";

      return (
        <TouchableOpacity
          activeOpacity={0.75}
          style={styles.collapsibleSectionHeader}
          onPress={() => toggleSectionCollapsed(section.date)}
        >
          <View style={styles.collapsibleSectionHeaderRow}>
            <View style={styles.collapsibleSectionHeaderTextWrap}>
              <Text style={styles.collapsibleSectionHeaderText}>
                {section.title || section.date}
              </Text>
              <Text style={styles.collapsibleSectionHeaderCount}>
                {itemCount} {itemLabel}
              </Text>
            </View>
            {isCollapsed ? (
              <ChevronRight size={18} color="#666666" />
            ) : (
              <ChevronDown size={18} color="#666666" />
            )}
          </View>
          <View style={styles.sectionHeaderDivider} />
        </TouchableOpacity>
      );
    },
    [
      collapsedSections,
      isCollapsibleSection,
      renderSectionHeader,
      toggleSectionCollapsed,
    ],
  );

  const renderSectionFooter = useCallback(
    ({ section }) => {
      if (!isCollapsibleSection(section) || collapsedSections[section.date]) {
        return null;
      }

      const itemCount =
        typeof section.__itemCount === "number"
          ? section.__itemCount
          : section.data?.length || 0;

      if (itemCount < 1) {
        return null;
      }

      return (
        <View style={styles.sectionFooterContainer}>
          <SecondaryButton
            text="Add appointment"
            onPress={() => onOpenQuickRecord?.(section.date)}
            fullWidth
            style={styles.addAppointmentButton}
          />
        </View>
      );
    },
    [collapsedSections, isCollapsibleSection, onOpenQuickRecord],
  );

  return (
    <View style={styles.baseViewContainer}>
      <View style={styles.listControlsWrapper}>
        <DateControlHeader
          viewLabel={viewLabel}
          viewMode={viewMode}
          toggleViewMode={toggleViewMode}
          onOpenRangePicker={onOpenRangePicker}
          onShiftRange={onShiftRange}
          onTodayPress={onTodayPress}
          rangeOffset={rangeOffset}
          tipsEnabled={tipsEnabled}
        />
      </View>
      {/* Loading state */}
      {isLoading && (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#0085FF" />
          <Text style={styles.loadingText}>Loading consults...</Text>
        </View>
      )}

      {/* Error state */}
      {isError && (
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>
            Error loading patients. Please try again later.
          </Text>
          <TouchableOpacity
            style={styles.retryButton}
            onPress={() => {
              trackEvent("Retry Schedule Load", {}, session);
              refreshSchedule();
            }}
          >
            <Text style={styles.retryButtonText}>Retry</Text>
          </TouchableOpacity>
        </View>
      )}

      {/* Keep patient search available even when the selected range is empty. */}
      {!isLoading && !isError && (
        <View style={styles.searchContainer}>
          <SearchInput
            value={searchTerm}
            onChangeText={handleSearchTermChange}
            placeholder="Search patients by name, email, or phone..."
            loading={isSearching}
            onClear={() => {
              trackEvent(
                "Clear Search Input",
                {
                  "Search Term": searchTerm,
                },
                session,
              );
              setSearchTerm("");
              setSearchResults([]);
              setSearchMode(false);
            }}
            inputStyle={styles.searchInput}
          />
        </View>
      )}

      {/* Case 1: No EHR connected - Product description empty state */}
      {!isLoading && !isError && showEmptyState && !ehrConnected && (
        <View style={styles.emptyStateContainer}>
          <View style={styles.emptyStateCard}>
            <Image
              source={{ uri: "/api/placeholder/120/120" }}
              style={styles.emptyStateIcon}
            />
            <Text style={styles.emptyStateTitle}>
              Streamline Your Patient Consultations
            </Text>
            <Text style={styles.emptyStateDescription}>
              Enhance your patient consultation workflow with powerful tools for
              simulations, similar patient comparisons, and integrated
              scheduling. Connect your electronic health record system to get
              started.
            </Text>
            <EMRIntegrationRequestPrompt session={session} />
            <TouchableOpacity
              style={styles.connectEhrButton}
              onPress={() => {
                trackEvent("Connect EHR Button Clicked", {}, session);
              }}
            >
              <Text style={styles.connectEhrButtonText}>
                Connect EHR System
              </Text>
            </TouchableOpacity>
            <SecondaryButton
              text="Add appointment manually"
              onPress={() => onOpenQuickRecord?.()}
              fullWidth
              style={styles.addAppointmentButton}
            />
            {!useDummyData && (
              <SecondaryButton
                text="Use Demo Data For Testing"
                onPress={() => onUseDemoDataForTesting?.()}
                fullWidth
                style={styles.useDemoDataButton}
              />
            )}
          </View>
        </View>
      )}

      {/* Case 2: EHR connected but no patients scheduled */}
      {!isLoading && !isError && showEmptyState && ehrConnected && (
        <View style={styles.emptyStateContainer}>
          <View style={styles.emptyStateCard}>
            <Image
              source={{ uri: "/api/placeholder/120/120" }}
              style={styles.emptyStateIcon}
            />
            <Text style={styles.emptyStateTitle}>No Consults Scheduled</Text>
            <EMRIntegrationRequestPrompt session={session} />
            <SecondaryButton
              text="Add appointment manually"
              onPress={() => onOpenQuickRecord?.()}
              fullWidth
              style={styles.addAppointmentButton}
            />
            {!useDummyData && (
              <SecondaryButton
                text="Use Demo Data For Testing"
                onPress={() => onUseDemoDataForTesting?.()}
                fullWidth
                style={styles.useDemoDataButton}
              />
            )}
          </View>
        </View>
      )}

      {/* Show the schedule when populated, or global patient search results. */}
      {!isLoading && !isError && showScheduleOrSearchResults && (
        <>
          {/* Section List grouped by date */}
          <SectionList
            ref={scrollRef}
            style={styles.sectionList}
            sections={displayedSections}
            renderItem={renderPatientItem}
            renderSectionHeader={renderSectionHeaderWithCollapse}
            renderSectionFooter={renderSectionFooter}
            keyExtractor={(item) => item.id.toString()}
            contentContainerStyle={[
              styles.sectionListContent,
              { paddingTop: headerHeight }, // Add padding to avoid content being under the sticky header
            ]}
            stickySectionHeadersEnabled={false} // We're handling our own sticky headers
            showsVerticalScrollIndicator={true}
            onScroll={(e) => {
              const offsetY = e.nativeEvent.contentOffset.y;
              setScrollY(offsetY);

              // Determine which section is currently visible
              let newVisibleSection = 0;
              let totalOffset = 0;

              for (let i = 0; i < displayedSections.length; i++) {
                const sectionHeight =
                  headerHeight + displayedSections[i].data.length * 110;
                totalOffset += sectionHeight;

                if (offsetY < totalOffset - headerHeight / 2) {
                  newVisibleSection = i;
                  break;
                }
              }

              if (newVisibleSection !== visibleSectionIndex) {
                setVisibleSectionIndex(newVisibleSection);
              }
            }}
            scrollEventThrottle={16} // For smoother updates
            onScrollToIndexFailed={(info) => {
              console.warn("Failed to scroll to section", info);

              // Workaround: use flattened index instead of section/item indices
              const wait = new Promise((resolve) => setTimeout(resolve, 500));
              wait.then(() => {
                if (
                  scrollRef.current &&
                  info.index < displayedSections.length
                ) {
                  // Try a different approach - scroll to offset instead
                  scrollRef.current.scrollToOffset({
                    offset: info.averageItemLength * info.index,
                    animated: true,
                  });
                }
              });
            }}
            initialNumToRender={20} // Render more items initially
            maxToRenderPerBatch={10} // Process items in smaller batches
            windowSize={10} // Reduce render window for better performance
            onEndReached={() => {
              // Track Load More Days event
              trackEvent(
                "Load More Days",
                {
                  Direction: "past",
                  Days: 7,
                },
                session,
              );

              // Load more past days when reaching the end
              loadMoreDays("past", 7);
            }}
            onEndReachedThreshold={0.5}
          />
        </>
      )}
    </View>
  );
};

// Header Controls component for PatientListView
const styles = StyleSheet.create({
  // Base View Container
  baseViewContainer: {
    flex: 1,
    position: "relative",
    minWidth: 0,
  },
  listControlsWrapper: {
    marginBottom: 0,
    width: "100%",
    minWidth: 0,
  },

  // Loading and Error States
  loadingContainer: {
    padding: 24,
    alignItems: "center",
    justifyContent: "center",
  },
  loadingText: {
    fontSize: 16,
    color: "#555",
    marginTop: 12,
  },
  errorContainer: {
    padding: 24,
    alignItems: "center",
    justifyContent: "center",
  },
  errorText: {
    fontSize: 16,
    color: "#e53935",
    marginBottom: 16,
  },
  retryButton: {
    backgroundColor: "#0085FF",
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 6,
  },
  retryButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "500",
  },

  // Search Bar
  searchContainer: {
    paddingHorizontal: 20,
    paddingVertical: 10,
    backgroundColor: "#FFFFFF",
    zIndex: 1,
    minWidth: 0,
  },
  searchInput: {
    fontSize: 18, // Increased from 16
    height: 40,
  },

  // Section List
  sectionList: {
    flex: 1,
    minWidth: 0,
  },
  sectionListContent: {
    paddingBottom: 100, // Extra padding for Paige
    minWidth: 0,
  },
  collapsibleSectionHeader: {
    backgroundColor: "#F5F5F5",
    paddingVertical: 16,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
    borderBottomColor: "#E0E0E0",
  },
  collapsibleSectionHeaderRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  collapsibleSectionHeaderTextWrap: {
    flex: 1,
    marginRight: 12,
  },
  collapsibleSectionHeaderText: {
    fontSize: 18,
    fontWeight: "600",
    color: "#333333",
  },
  collapsibleSectionHeaderCount: {
    fontSize: 13,
    color: "#777777",
    marginTop: 4,
  },
  sectionHeaderDivider: {
    height: 2,
    width: 40,
    backgroundColor: "#0085FF",
    marginTop: 8,
  },

  // Empty State Styles
  emptyStateContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
    minHeight: 400,
    paddingBottom: 120, // Extra padding for Paige
  },
  emptyStateCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    padding: 24,
    alignItems: "center",
    width: "100%",
    maxWidth: 500,
    shadowColor: "#000000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  emptyStateIcon: {
    width: 80,
    height: 80,
    marginBottom: 24,
  },
  emptyStateTitle: {
    fontSize: 22,
    fontWeight: "600",
    color: "#333333",
    marginBottom: 16,
    textAlign: "center",
  },
  emptyStateDescription: {
    fontSize: 16,
    color: "#666666",
    textAlign: "center",
    marginBottom: 32,
    lineHeight: 24,
  },
  connectEhrButton: {
    backgroundColor: "#0085FF",
    paddingHorizontal: 24,
    paddingVertical: 14,
    borderRadius: 8,
    width: "100%",
    alignItems: "center",
    marginBottom: 12,
  },
  connectEhrButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "500",
  },
  addAppointmentButton: {
    marginTop: 12,
  },
  useDemoDataButton: {
    marginTop: 12,
  },
  sectionFooterContainer: {
    paddingHorizontal: 20,
    paddingVertical: 10,
  },
});

export default PatientListView;
